import { t, Trans } from '@lingui/macro';
import { IconButton } from '@mui/material';
import { CaretLeft, CaretRight, FilePdf } from '@phosphor-icons/react';
import { PdfViewer } from 'components/pdfViewer/PdfViewer';
import type { PdfPagerInfo } from 'components/pdfViewer/PdfViewer';
import {
  OfWord,
  PageNavFraction,
  PageNumberField,
  PreviewToolbarFloating,
  TotalPagesLabel,
} from 'components/pdfViewer/pdfViewer.styles';
import { Text } from 'components/text/Text';
import { colors } from 'styles/theme/colors';
import { isPdfFile } from '../utils';
import { FilePreviewRoot, ImgPreviewClip, NonPdfFallback, PreviewImg } from './filePreview.styles';
import { FilePreviewPdfScrollSync } from './FilePreviewPdfScrollSync';

export type FilePreviewProps = {
  file: File;
  previewUrl: string;
  hideNavigationShowScrollSync?: boolean;
  onPdfPagerInfo?: (info: PdfPagerInfo) => void;
};

/** Static, fully disabled `Page 1 of 1` pager shown under image / fallback previews so the
 * toolbar row keeps its place when the active file is not a PDF. */
const StaticPagerToolbar = () => (
  <PreviewToolbarFloating>
    <IconButton
      size="sm"
      aria-label={t`Previous`}
      disabled
      sx={{ color: colors.grey[600], '&.Mui-disabled': { opacity: 1 } }}
    >
      <CaretLeft size={18} weight="bold" color={colors.grey[600]} />
    </IconButton>
    <PageNavFraction aria-label={t`Page 1 of 1`}>
      <PageNumberField
        type="text"
        inputMode="numeric"
        aria-label={t`Page`}
        value="1"
        disabled
        readOnly
      />
      <OfWord>{t`of`}</OfWord>
      <TotalPagesLabel variant="bodySm">1</TotalPagesLabel>
    </PageNavFraction>
    <IconButton
      size="sm"
      aria-label={t`Next`}
      disabled
      sx={{ color: colors.grey[600], '&.Mui-disabled': { opacity: 1 } }}
    >
      <CaretRight size={18} weight="bold" color={colors.grey[600]} />
    </IconButton>
  </PreviewToolbarFloating>
);

/**
 * File preview panel: PDFs render through the shared `<PdfViewer>` (canvas +
 * floating pager — promoted from this file, DL-61574), images inline, anything
 * else a fallback. `hideNavigationShowScrollSync` keeps the stacked scroll-sync
 * pager for the certificate modal.
 */
export const FilePreview = ({
  file,
  previewUrl,
  hideNavigationShowScrollSync = false,
  onPdfPagerInfo,
}: FilePreviewProps) => {
  const activeIsPdf = isPdfFile(file);

  if (hideNavigationShowScrollSync && activeIsPdf) {
    return (
      <FilePreviewRoot>
        <FilePreviewPdfScrollSync file={file} onPagerInfo={onPdfPagerInfo} />
      </FilePreviewRoot>
    );
  }

  if (activeIsPdf) {
    return (
      <FilePreviewRoot>
        <PdfViewer
          // Remount per selected file so the viewer never carries a stale page index over.
          key={previewUrl}
          source={file}
          ariaLabel={file.name}
          onPagerInfo={onPdfPagerInfo}
          hideToolbar={hideNavigationShowScrollSync}
        />
      </FilePreviewRoot>
    );
  }

  return (
    <FilePreviewRoot>
      {file.type.startsWith('image/') ? (
        <ImgPreviewClip>
          <PreviewImg src={previewUrl} alt="" />
        </ImgPreviewClip>
      ) : (
        <NonPdfFallback>
          <FilePdf size={48} weight="duotone" color={colors.brand[600]} />
          <Text variant="bodySm" color="secondary" top={8} align="center">
            <Trans>Preview not available</Trans>
          </Text>
        </NonPdfFallback>
      )}
      {!hideNavigationShowScrollSync && <StaticPagerToolbar />}
    </FilePreviewRoot>
  );
};
